import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { brand, externalLinks } from "@/data/mockData";

const simpleLinks = [
  { to: "/", label: "Home" },
  // "Why Me" is rendered as a dropdown (About Me + Services)
  // "Listings" placed after Why Me as a simple route
  { to: "/listings", label: "Listings" },
  { to: "/framework", label: "Framework" },
];

const whyMeItems = [
  { to: "/about", label: "About Me", desc: "Johnny's story & approach" },
  { to: "/services", label: "Services", desc: "Consulting & marketing solutions" },
];

const successStoriesItems = [
  { to: "/results", label: "Proven Results", desc: "Client outcomes & case studies" },
  { to: "/testimonials", label: "Testimonials", desc: "What clients say" },
  { to: "/awards", label: "Awards", desc: "Industry recognition" },
];

const resourcesItems = [
  { to: externalLinks.propertyValuation, label: "Property Valuation", desc: "Free instant valuation", external: true },
  { to: "/calculators", label: "Calculators", desc: "Mortgage · Sales Proceed · Purchase" },
  { to: "/blog", label: "Blog", desc: "Insights & market views" },
];

function DesktopDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="relative"
    >
      <button
        type="button"
        data-testid={`nav-dropdown-${testId}`}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 hover:text-[#0a2540] transition-colors"
      >
        {label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72"
          >
            <div className="bg-white border border-gray-100 shadow-xl rounded-md overflow-hidden">
              {items.map((it) =>
                it.external ? (
                  <a
                    key={it.label}
                    href={it.to}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`nav-dropdown-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block px-5 py-3.5 hover:bg-[#fffbea] transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-serif text-base text-[#0a2540]">{it.label}</div>
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs text-slate-400 group-hover:text-[#0a2540]" aria-hidden="true" />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
                  </a>
                ) : (
                  <Link
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-dropdown-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block px-5 py-3.5 hover:bg-[#fffbea] transition-colors"
                  >
                    <div className="font-serif text-base text-[#0a2540]">{it.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
                  </Link>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileExpand, setMobileExpand] = useState(null); // 'success' | 'resources' | null
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileExpand(null);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm"
          : "bg-white/90 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Brand: MeProperty logo only + Johnny Tan name */}
          <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3 group">
            <img
              src={brand.logoUrl}
              alt="Johnny Tan"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-gray-100 transition-transform group-hover:scale-105"
              loading="lazy"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif font-semibold text-[#0a2540] text-lg tracking-tight">
                Johnny Tan
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
                Singapore Property Wealth Advisor
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            <NavLink
              to="/"
              end
              data-testid="nav-link-home"
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0a2540]" : "text-slate-700 hover:text-[#0a2540]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  Home
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#ffde59]"
                    />
                  )}
                </>
              )}
            </NavLink>
            <DesktopDropdown label="Why Me" items={whyMeItems} testId="why-me" />
            {simpleLinks.filter((l) => l.to !== "/").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#0a2540]" : "text-slate-700 hover:text-[#0a2540]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-[#ffde59]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            <DesktopDropdown label="Success Stories" items={successStoriesItems} testId="success-stories" />
            <DesktopDropdown label="Resources" items={resourcesItems} testId="resources" />
            <NavLink
              to="/contact"
              data-testid="nav-link-contact"
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-[#0a2540]" : "text-slate-700 hover:text-[#0a2540]"
                }`
              }
            >
              Contact
            </NavLink>
          </nav>

          {/* Schedule CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-schedule-btn"
              className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-6 py-3 text-sm font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all rounded-full"
            >
              Schedule an Appointment
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="xl:hidden p-2 text-[#0a2540]"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="xl:hidden border-t border-gray-100 bg-white overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-4 py-4 flex flex-col gap-1 max-h-[calc(100vh-5rem)] overflow-y-auto">
              <NavLink
                to="/"
                end
                data-testid="nav-mobile-link-home"
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium rounded ${
                    isActive ? "bg-[#fffbea] text-[#0a2540]" : "text-slate-700"
                  }`
                }
              >
                Home
              </NavLink>

              {/* Why Me collapsible */}
              <button
                onClick={() => setMobileExpand((m) => (m === "whyme" ? null : "whyme"))}
                className="flex items-center justify-between px-3 py-3 text-base font-medium text-slate-700"
                data-testid="nav-mobile-why-me"
              >
                Why Me
                <ChevronDown size={16} className={`transition-transform ${mobileExpand === "whyme" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "whyme" && (
                <div className="ml-3 mb-2 flex flex-col gap-1">
                  {whyMeItems.map((it) => (
                    <Link key={it.to} to={it.to} className="px-3 py-2.5 text-sm text-slate-600 hover:text-[#0a2540]">
                      <span className="font-medium">{it.label}</span>
                      <span className="block text-xs text-slate-400">{it.desc}</span>
                    </Link>
                  ))}
                </div>
              )}

              {simpleLinks.filter((l) => l.to !== "/").map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-medium rounded ${
                      isActive ? "bg-[#fffbea] text-[#0a2540]" : "text-slate-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Success Stories collapsible */}
              <button
                onClick={() => setMobileExpand((m) => (m === "success" ? null : "success"))}
                className="flex items-center justify-between px-3 py-3 text-base font-medium text-slate-700"
                data-testid="nav-mobile-success-stories"
              >
                Success Stories
                <ChevronDown size={16} className={`transition-transform ${mobileExpand === "success" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "success" && (
                <div className="ml-3 mb-2 flex flex-col gap-1">
                  {successStoriesItems.map((it) => (
                    <Link key={it.to} to={it.to} className="px-3 py-2.5 text-sm text-slate-600 hover:text-[#0a2540]">
                      <span className="font-medium">{it.label}</span>
                      <span className="block text-xs text-slate-400">{it.desc}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Resources collapsible */}
              <button
                onClick={() => setMobileExpand((m) => (m === "resources" ? null : "resources"))}
                className="flex items-center justify-between px-3 py-3 text-base font-medium text-slate-700"
                data-testid="nav-mobile-resources"
              >
                Resources
                <ChevronDown size={16} className={`transition-transform ${mobileExpand === "resources" ? "rotate-180" : ""}`} />
              </button>
              {mobileExpand === "resources" && (
                <div className="ml-3 mb-2 flex flex-col gap-1">
                  {resourcesItems.map((it) =>
                    it.external ? (
                      <a key={it.label} href={it.to} target="_blank" rel="noreferrer" className="px-3 py-2.5 text-sm text-slate-600 hover:text-[#0a2540]">
                        <span className="font-medium">{it.label} ↗</span>
                        <span className="block text-xs text-slate-400">{it.desc}</span>
                      </a>
                    ) : (
                      <Link key={it.to} to={it.to} className="px-3 py-2.5 text-sm text-slate-600 hover:text-[#0a2540]">
                        <span className="font-medium">{it.label}</span>
                        <span className="block text-xs text-slate-400">{it.desc}</span>
                      </Link>
                    )
                  )}
                </div>
              )}

              <NavLink
                to="/contact"
                data-testid="nav-mobile-link-contact"
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium rounded ${
                    isActive ? "bg-[#fffbea] text-[#0a2540]" : "text-slate-700"
                  }`
                }
              >
                Contact
              </NavLink>

              <Link
                to="/contact"
                data-testid="nav-mobile-schedule-btn"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#ffde59] text-[#023e8a] px-6 py-3.5 text-sm font-semibold rounded-full"
              >
                Schedule an Appointment
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
